/**
 * Month Calendar View Component
 * Outlook-style monthly calendar grid
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Chip,
  useTheme,
  alpha,
} from '@mui/material'
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  format,
  parseISO,
} from 'date-fns'
import { CalendarEventDto } from '@api/types/calendarEvent'

interface MonthCalendarViewProps {
  currentDate: Date
  events: CalendarEventDto[]
  onDateClick: (date: Date) => void
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MAX_EVENTS_SHOWN = 3

export function MonthCalendarView({
  currentDate,
  events,
  onDateClick,
}: MonthCalendarViewProps) {
  const theme = useTheme()
  const navigate = useNavigate()

  // Generate calendar days for the month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }, [currentDate])

  // Group events by date
  const eventsByDate = useMemo(() => {
    const grouped: Record<string, CalendarEventDto[]> = {}
    events.forEach((event) => {
      const dateKey = format(parseISO(event.startDateTime), 'yyyy-MM-dd')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(event)
    })
    return grouped
  }, [events])

  const getEventsForDate = (date: Date): CalendarEventDto[] => {
    const dateKey = format(date, 'yyyy-MM-dd')
    return eventsByDate[dateKey] || []
  }

  const handleEventClick = (e: React.MouseEvent, eventId: string) => {
    e.stopPropagation()
    navigate(`/events/${eventId}`)
  }

  // Split days into weeks for grid
  const weeks = useMemo(() => {
    const result: Date[][] = []
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7))
    }
    return result
  }, [calendarDays])

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* Weekday Headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'grey.100',
        }}
      >
        {WEEKDAYS.map((day) => (
          <Box
            key={day}
            sx={{
              py: 1,
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.secondary',
            }}
          >
            {day}
          </Box>
        ))}
      </Box>

      {/* Calendar Grid */}
      <Box>
        {weeks.map((week, weekIndex) => (
          <Box
            key={weekIndex}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              borderBottom: weekIndex < weeks.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
            }}
          >
            {week.map((day) => {
              const dayEvents = getEventsForDate(day)
              const isCurrentMonth = isSameMonth(day, currentDate)
              const isCurrentDay = isToday(day)

              return (
                <Box
                  key={day.toISOString()}
                  onClick={() => onDateClick(day)}
                  sx={{
                    minHeight: 100,
                    minWidth: 0, // Prevent grid blowout from long content
                    overflow: 'hidden',
                    p: 0.5,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    '&:last-child': { borderRight: 'none' },
                    bgcolor: isCurrentMonth ? 'background.paper' : 'grey.50',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: isCurrentMonth
                        ? alpha(theme.palette.primary.main, 0.04)
                        : 'grey.100',
                    },
                  }}
                >
                  {/* Day Number */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        width: 28,
                        height: 28,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        fontWeight: isCurrentDay ? 600 : 400,
                        color: !isCurrentMonth
                          ? 'text.disabled'
                          : isCurrentDay
                            ? 'primary.contrastText'
                            : 'text.primary',
                        bgcolor: isCurrentDay ? 'primary.main' : 'transparent',
                      }}
                    >
                      {format(day, 'd')}
                    </Typography>
                  </Box>

                  {/* Events */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                    {dayEvents.slice(0, MAX_EVENTS_SHOWN).map((event) => (
                      <Chip
                        key={event.id}
                        label={event.title}
                        size="small"
                        onClick={(e) => handleEventClick(e, event.id)}
                        sx={{
                          height: 20,
                          maxWidth: '100%',
                          fontSize: '0.7rem',
                          justifyContent: 'flex-start',
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            px: 0.5,
                          },
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                        }}
                      />
                    ))}
                    {dayEvents.length > MAX_EVENTS_SHOWN && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.65rem',
                          color: 'text.secondary',
                          pl: 0.5,
                        }}
                      >
                        +{dayEvents.length - MAX_EVENTS_SHOWN} more
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        ))}
      </Box>
    </Paper>
  )
}
